import React, { useContext } from 'react';
import proptypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { ThumbsUpThumbsDownWithModal } from '@thd-olt-component-react/user-feedback';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import {
  getMcvisID,
  getPageType,
  getSvocId
} from '../../../utils/window-utils';
import { getPageKey } from '../assistant-utils';
import { useAssistant } from '../AssistantProvider';
import { useDrawer } from '../DrawerProvider';
import {
  FEEDBACK_ANALYTICS_MAA_PRIMARY_CATEGORY,
  FEEDBACK_ANALYTICS_MAA_EVENT_NAME_PREFIX,
  MESSAGE_ORIGIN_UNSPECIFIED,
  FALLBACK_SESSION_ID,
  FEEDBACK_CLICK_COMMENT_QUESTION_ORIGIN_MAA
} from '../constants';

const { arrayOf, bool, number, shape, string } = proptypes;

const MessageFeedback = ({ message }) => {
  const { zipCode, channel } = useContext(ExperienceContext);
  const storeId = useStoreId();
  const { setFeedbackProvided, session } = useAssistant();
  const pageType = getPageType();
  const { forceCloseForm } = useDrawer();

  const questionOrigin = message?.userMessageOrigin || MESSAGE_ORIGIN_UNSPECIFIED;
  const userQuestion = message?.content?.responseSummary?.userQuestion || '';
  const feedbackQuestion = `${questionOrigin}:${userQuestion}`;

  const feedbackAnalytics = {
    primaryCategory: FEEDBACK_ANALYTICS_MAA_PRIMARY_CATEGORY,
    eventName: FEEDBACK_ANALYTICS_MAA_EVENT_NAME_PREFIX,
    sessionID: session.sessionId,
    question: `${FEEDBACK_CLICK_COMMENT_QUESTION_ORIGIN_MAA}:${feedbackQuestion}`,
    fallbackSessionID: FALLBACK_SESSION_ID,
    feedbackClickCommentOrigin: FEEDBACK_CLICK_COMMENT_QUESTION_ORIGIN_MAA
  };

  const handleFeedbackData = () => {
    setFeedbackProvided(message);

    return {
      magicApronAssistant: {
        userContext: {
          userIdentities: {
            mcvisId: getMcvisID(),
            svocId: getSvocId(),
          },
          sessionId: session.sessionId,
          storeId,
          zipCode,
          deviceType: channel === 'mobile' ? 'Mobile' : 'Desktop',
          language: 'en-US',
        },
        pageContext: {
          pageType,
          referrerPage: window.document.referrer,
          timestamp: message?.content?.timestamp,
        },
        responseSummary: message.content?.responseSummary,
        relatedContent: message.content?.relatedContent,
        suggestedQuestions: message.content?.suggestedQuestions,
        followUpQuestions: message.content?.followUpQuestions,
        additionalDetails: message.content?.additionalDetails,
        error: message?.error?.message,
      }
    };
  };

  return (
    <ThumbsUpThumbsDownWithModal
      anchorKey={getPageKey()}
      anchorType={pageType}
      customerInsights="Assistant"
      handleFeedbackData={handleFeedbackData}
      feedbackAnalytics={feedbackAnalytics}
      successMessage="Thank you. Your feedback helps us improve."
      thanksMessageHideTime={4000}
      forceCloseForm={forceCloseForm}
    />
  );
};

MessageFeedback.propTypes = {
  message: shape({
    content: shape({
      timestamp: number,
      feedbackProvided: bool,
      relatedContent: shape({
        relatedSearchResults: arrayOf(shape({
          itemId: string,
          price: string,
          imageUrl: string,
          rating: string,
          description: string,
          href: string,
          category: arrayOf(string),
          searchTerm: string
        })),
        recommendations: shape({
          additionalProperties: arrayOf(shape({
            itemId: string,
            price: string,
            averageRating: string,
            imageUrl: string,
            href: string,
            productName: string
          }))
        })
      }),
      suggestedQuestions: arrayOf(string),
      followUpQuestions: arrayOf(string),
      additionalDetails: shape({
        inquiryDetails: shape({
          telephoneNumber: string,
          description: string,
          referenceUrl: string
        })
      })
    })
  }).isRequired,
};

MessageFeedback.defaultProps = {};

MessageFeedback.dataModel = extend(
  ThumbsUpThumbsDownWithModal,
);

export { MessageFeedback };
